import { z } from 'zod';
import { PRICING_TYPE_ENUM } from '~/purchase/schemas';
import { IPricingConfigurationType } from '~/purchase/types';

export const EXTRA_BONUS_MIN = 0;
export const EXTRA_BONUS_MAX = 100;

export const EXTRA_BONUS_OUT_OF_RANGE_MESSAGE =
  'app.common.validations.percent_field';

export const EXTRA_BONUS_NEGATIVE_MESSAGE =
  'app.common.validations.positive_number';

const isFilled = (value?: number | null): value is number =>
  null !== value && undefined !== value && !Number.isNaN(value);

/**
 * A discount and a margin are percentages, so they are bounded on both ends; a net margin is an amount
 * in the value's currency, which only has to stay positive. The percentage rule mirrors the one the API
 * enforces on the payload, so the save is refused here rather than by a round trip.
 */
const violationOf = (
  bonusType: IPricingConfigurationType,
  value?: number | null,
): string | null => {
  if (!isFilled(value)) {
    return null;
  }

  if (PRICING_TYPE_ENUM.NET_MARGIN === bonusType) {
    return value < EXTRA_BONUS_MIN ? EXTRA_BONUS_NEGATIVE_MESSAGE : null;
  }

  return value < EXTRA_BONUS_MIN || value > EXTRA_BONUS_MAX
    ? EXTRA_BONUS_OUT_OF_RANGE_MESSAGE
    : null;
};

export const distributionExtraBonusDraftSchema = z
  .object({
    bonusType: z.enum(PRICING_TYPE_ENUM),
    hqBonus: z.number().nullable(),
    values: z.record(z.string(), z.number().nullable()),
  })
  .superRefine((draft, ctx) => {
    const hqBonusViolation = violationOf(draft.bonusType, draft.hqBonus);

    if (hqBonusViolation) {
      ctx.addIssue({
        code: 'custom',
        path: ['hqBonus'],
        message: hqBonusViolation,
      });
    }

    Object.entries(draft.values ?? {}).forEach(([key, value]) => {
      const violation = violationOf(draft.bonusType, value);

      if (violation) {
        ctx.addIssue({
          code: 'custom',
          path: ['values', key],
          message: violation,
        });
      }
    });
  });
